import type {
  Aggregate,
  Candidate,
  Requisition,
  RequisitionDetail,
  RequisitionSummary,
  Scorecard,
  ScorecardInput,
  User,
} from "@ld-scorecard/shared";
import { useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { useConfig } from "./config";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export class ApiClient {
  constructor(
    private baseUrl: string,
    private getToken: () => string | undefined,
  ) {}

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = this.getToken();
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(body ? { "content-type": "application/json" } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const data = await res.json();
        if (data?.error) message = data.error;
      } catch {
        /* keep statusText */
      }
      throw new ApiError(res.status, message);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  me = () => this.request<User>("GET", "/me");

  // Requisitions (the top-level object)
  listRequisitions = () => this.request<RequisitionSummary[]>("GET", "/requisitions");
  getRequisition = (reqId: string) =>
    this.request<RequisitionDetail>("GET", `/requisitions/${reqId}`);
  createRequisition = (input: {
    title: string;
    department?: string;
    hiringManager?: string;
    greenhouseUrl?: string;
  }) => this.request<Requisition>("POST", "/requisitions", input);
  updateRequisition = (reqId: string, patch: Partial<Requisition>) =>
    this.request<Requisition>("PATCH", `/requisitions/${reqId}`, patch);
  deleteRequisition = (reqId: string) =>
    this.request<{ ok: boolean }>("DELETE", `/requisitions/${reqId}`);
  addCandidate = (reqId: string, input: { name: string; panelDate?: string }) =>
    this.request<Candidate>("POST", `/requisitions/${reqId}/candidates`, input);

  // Candidate-scoped
  getCandidate = (id: string) => this.request<Candidate>("GET", `/candidates/${id}`);
  updateCandidate = (id: string, patch: Partial<Candidate>) =>
    this.request<Candidate>("PATCH", `/candidates/${id}`, patch);
  deleteCandidate = (id: string) =>
    this.request<{ ok: boolean }>("DELETE", `/candidates/${id}`);

  getMyScorecard = (id: string) =>
    this.request<Scorecard>("GET", `/candidates/${id}/scorecard`);
  saveScorecard = (id: string, input: ScorecardInput) =>
    this.request<Scorecard>("PUT", `/candidates/${id}/scorecard`, input);

  getAggregate = (id: string) =>
    this.request<Aggregate>("GET", `/candidates/${id}/aggregate`);
  getScorecards = (id: string) =>
    this.request<Scorecard[]>("GET", `/candidates/${id}/scorecards`);
  deleteScorecard = (id: string, email: string) =>
    this.request<{ ok: boolean }>(
      "DELETE",
      `/candidates/${id}/scorecards/${encodeURIComponent(email)}`,
    );

  async downloadExport(id: string): Promise<Blob> {
    const token = this.getToken();
    const res = await fetch(`${this.baseUrl}/candidates/${id}/export`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, res.statusText);
    return res.blob();
  }

  listUsers = () => this.request<User[]>("GET", "/users");
  addUser = (input: { email: string; role: string; name?: string }) =>
    this.request<User>("POST", "/users", input);
  removeUser = (email: string) =>
    this.request<{ ok: boolean }>("DELETE", `/users/${encodeURIComponent(email)}`);
}

/** Hook that builds an ApiClient wired to the current auth token. */
export function useApi(): ApiClient {
  const config = useConfig();
  const auth = useAuth();
  const token = auth.user?.id_token;
  return useMemo(
    () => new ApiClient(config.apiBaseUrl, () => token),
    [config.apiBaseUrl, token],
  );
}
